/**
 * Web Push Notifications (PWA)
 *
 * Registers the service worker, requests browser permission, creates a
 * PushSubscription and syncs it with the backend.
 *
 * Usage:
 *   import { initWebPush, teardownWebPush } from './pushNotifications';
 *   await initWebPush(token);   // call after login
 *   await teardownWebPush(token); // call after logout
 */

import api from './api';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert a URL-safe base64 string (VAPID public key) to a Uint8Array
 * as required by pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/** Return true if the browser supports service workers and the Push API. */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/** Fetch the VAPID public key from the backend. */
async function getVapidPublicKey() {
  const vapid = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (vapid) return vapid;
  // Fall back to fetching from the API (useful when env var not set at build time)
  try {
    const res = await api.get('/push/vapid-key');
    return res.data?.data?.publicKey || null;
  } catch {
    return null;
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Register the service worker, subscribe to push, and sync with the backend.
 * Safe to call on every login — it is idempotent.
 *
 * @param {string} authToken  JWT token (used internally by the api interceptor)
 * @returns {Promise<boolean>} true if notifications are now active
 */
export async function initWebPush(authToken) {
  console.log('[push] initWebPush called');
  
  if (!isPushSupported()) {
    console.log('[push] Web push not supported in this browser.');
    return false;
  }

  console.log('[push] Push is supported, proceeding...');

  try {
    // 1. Register (or reuse) the service worker
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('[push] Service worker registered:', registration.scope);

    // 2. Ask for permission (no-op if already granted/denied)
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[push] Notification permission', permission);
      return false;
    }

    // 3. Get the VAPID public key
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      console.warn('[push] VAPID public key unavailable.');
      return false;
    }

    // 4. Subscribe (browser contacts the push service and returns an endpoint)
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    // 5. Send the subscription to our backend
    await api.post('/push/web-subscribe', subscription.toJSON());
    console.log('[push] Web push subscription active.');
    return true;

  } catch (err) {
    console.error('[push] initWebPush failed:', err);
    return false;
  }
}

/**
 * Unsubscribe from push and inform the backend.
 * Call on logout so the device stops receiving notifications.
 */
export async function teardownWebPush() {
  if (!isPushSupported()) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    // Tell the backend first so it removes the record
    try {
      await api.post('/push/web-unsubscribe', { endpoint: subscription.endpoint });
    } catch (err) {
      console.warn('[push] Backend unsubscribe call failed:', err);
    }

    await subscription.unsubscribe();
    console.log('[push] Web push unsubscribed.');
  } catch (err) {
    console.error('[push] teardownWebPush failed:', err);
  }
}
